import { useState } from 'react';

import { Popover, PopoverContent, PopoverTrigger } from 'cpool-components/general/Popover';
import { Tabs } from 'cpool-components/general/Tabs';
import type { ValueOf } from 'cpool-utils';
import { useTranslation } from 'react-i18next';
import { LuBell } from 'react-icons/lu';

import { ProcessedTab } from './ProcessedTab';
import { UnprocessedTab } from './UnprocessedTab';
import { HeaderButton } from '../../HeaderButton';
import { useNewDocuments } from '~/features/dashboard/hooks/useNewDocuments';
import { useResubmissions } from '~/features/dashboard/hooks/useResubmissions';
import { useUnreadConsorsNotes } from '~/features/dashboard/hooks/useUnreadConsorsNotes';
import { beginningOfTime } from '~/features/dashboard/utils/resubmissionDateRange';
import { useUnprocessedActiveUserSetLeads } from '~/features/notifications/hooks/useUnprocessedActiveUserSetLeads';
import { useUnprocessedAddedAssignedUserLeads } from '~/features/notifications/hooks/useUnprocessedAddedAssignedUserLeads';

const TAB_IDS = {
    PROCESSED: 'processed',
    UNPROCESSED: 'unprocessed',
} as const;

type TabId = ValueOf<typeof TAB_IDS>;

const isTabId = (value: string): value is TabId => Object.values(TAB_IDS).some((id) => id === value);

const useTotalUnprocessedCount = () => {
    // All overdue resubmissions (not just today's), kept current by notification pushes and the due timer.
    const { data: dueResubmissions } = useResubmissions({ start: beginningOfTime() });
    const { data: newDocuments } = useNewDocuments();
    const { data: unreadConsorsNotes } = useUnreadConsorsNotes();
    const addedAdvisorCount = useUnprocessedAddedAssignedUserLeads().length;
    const activeUserSetCount = useUnprocessedActiveUserSetLeads().length;

    const resubmissionsCount = dueResubmissions?.length ?? 0;
    const newDocumentsCount = newDocuments?.reduce((sum, lead) => sum + lead.unverifiedDocuments, 0) ?? 0;
    const bankReportsCount = unreadConsorsNotes?.length ?? 0;
    return resubmissionsCount + newDocumentsCount + bankReportsCount + addedAdvisorCount + activeUserSetCount;
};

export const NotificationsMenu = () => {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState<TabId>(TAB_IDS.UNPROCESSED);
    const [isOpen, setIsOpen] = useState(false);

    const closeMenu = () => setIsOpen(false);
    const hasUnprocessed = useTotalUnprocessedCount() > 0;

    return (
        <Popover open={isOpen} onOpenChange={setIsOpen}>
            <PopoverTrigger asChild>
                <HeaderButton iconLeft={<LuBell />}>
                    <span className="text-midnight">{t('TOP_HEADER.NOTIFICATIONS.TITLE')}</span>
                    {hasUnprocessed && (
                        <span
                            className="ml-2 inline-block size-3 rounded-full bg-alert-error"
                            data-testid="notifications-unread-indicator"
                        />
                    )}
                </HeaderButton>
            </PopoverTrigger>
            <PopoverContent side="bottom" align="end" sideOffset={8} className="w-[420px] p-6 shadow-2">
                <div className="flex flex-col gap-4">
                    <Tabs
                        tabs={[
                            { id: TAB_IDS.UNPROCESSED, label: t('TOP_HEADER.NOTIFICATIONS.UNPROCESSED') },
                            { id: TAB_IDS.PROCESSED, label: t('TOP_HEADER.NOTIFICATIONS.PROCESSED') },
                        ]}
                        selectedId={activeTab}
                        onSelect={(id) => {
                            if (isTabId(id)) setActiveTab(id);
                        }}
                        listClassName="flex w-full"
                    />
                    {activeTab === TAB_IDS.UNPROCESSED ? (
                        <UnprocessedTab onNavigate={closeMenu} />
                    ) : (
                        <ProcessedTab onNavigate={closeMenu} />
                    )}
                </div>
            </PopoverContent>
        </Popover>
    );
};
